import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Globe, Award, Clock, MapPin, Truck, CheckCircle, Network, DollarSign, Package, Users, FileText, Monitor, Download, MessageSquare, Search, Crosshair } from 'lucide-react';
import { useApp } from '../context/AppContext';

const HERO_BG = 'https://images.unsplash.com/photo-1680992046617-e2e35451bcdb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBzZXJ2ZXIlMjByb29tJTIwYmx1ZSUyMGxpZ2h0aW5nfGVufDB8fHx8MTc3MjQ3NjgzOXww&ixlib=rb-4.1.0&q=85';

const BRANDS = [
  { name: 'Dell', color: '#007DB8' },
  { name: 'HP', color: '#0096D6' },
  { name: 'Lenovo', color: '#E2231A' },
  { name: 'Apple', color: '#A3AAAE' },
];

const CATEGORIES = [
  { name: 'PC', desc: { en: 'Desktop Computers', es: 'Computadoras de Escritorio' }, count: '500+' },
  { name: 'NBK', desc: { en: 'Notebooks & Laptops', es: 'Portátiles y Laptops' }, count: '800+' },
  { name: 'AIO', desc: { en: 'All-in-One Systems', es: 'Sistemas Todo en Uno' }, count: '200+' },
  { name: 'Workstations', desc: { en: 'High Performance', es: 'Alto Rendimiento' }, count: '150+' },
  { name: 'LCD', desc: { en: 'Monitors & Displays', es: 'Monitores y Pantallas' }, count: '300+' },
  { name: 'Apple', desc: { en: 'Mac & MacBook', es: 'Mac y MacBook' }, count: '100+' },
];

const INVENTORY_EXAMPLES = [
  { brand: 'Dell', model: 'Latitude 5420', specs: 'i5 / 16GB / 512SSD', grade: 'A' },
  { brand: 'HP', model: 'EliteBook 840 G6', specs: 'i7 / 16GB / 256SSD', grade: 'A' },
  { brand: 'Lenovo', model: 'ThinkPad T480', specs: 'i5 / 8GB / 256SSD', grade: 'B' },
  { brand: 'Apple', model: 'MacBook Pro 2019', specs: 'i7 / 16GB / 512SSD', grade: 'A' },
];

export const HomePage = () => {
  const { t, language } = useApp();

  const features = [
    { icon: Shield, title: t('features.quality.title'), desc: t('features.quality.desc') },
    { icon: Globe, title: t('features.global.title'), desc: t('features.global.desc') },
    { icon: Award, title: t('features.selection.title'), desc: t('features.selection.desc') },
    { icon: Clock, title: t('features.experience.title'), desc: t('features.experience.desc') },
  ];

  return (
    <div data-testid="home-page">
      {/* 1. Hero Section — Updated */}
      <section
        className="relative min-h-screen flex items-center justify-center"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(5,5,5,0.7), rgba(5,5,5,0.9)), url(${HERO_BG})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="max-w-7xl mx-auto px-6 py-32 text-center">
          <h1
            className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 animate-fade-in-up"
            data-testid="hero-title"
          >
            <span className="gradient-text">{t('hero.title')}</span>
          </h1>
          <p
            className="text-xl md:text-2xl text-white/70 max-w-3xl mx-auto mb-12 animate-fade-in-up stagger-1"
            data-testid="hero-subtitle"
          >
            {t('hero.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up stagger-2">
            <Link
              to="/products"
              className="btn-primary flex items-center justify-center gap-2"
              data-testid="hero-cta"
            >
              {t('hero.cta')}
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contact"
              className="btn-secondary flex items-center justify-center gap-2"
              data-testid="hero-secondary"
            >
              {t('hero.secondary')}
            </Link>
          </div>
          <p className="text-white/40 text-sm mt-6 animate-fade-in-up stagger-2" data-testid="hero-tagline">
            {t('hero.tagline')}
          </p>
        </div>
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-white/50 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* 3. Trust Strip — NEW */}
      <section className="py-5 bg-[#0A0A0A] border-y border-white/5" data-testid="trust-strip">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap justify-center items-center gap-6 md:gap-12">
            {[
              { icon: Shield, label: t('home.trustStripCertified') },
              { icon: DollarSign, label: t('home.trustStripPricing') },
              { icon: Truck, label: t('home.trustStripExport') },
              { icon: CheckCircle, label: t('home.trustStripTested') },
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-2" data-testid={`trust-strip-${idx}`}>
                <item.icon className="w-4 h-4 text-[#00D4FF] flex-shrink-0" />
                <span className="text-white/60 text-xs md:text-sm">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DUAL PATH — Browse Inventory vs Custom Sourcing */}
      <section className="py-24 bg-[#0A0A0A]" data-testid="dual-path-section">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            {t('rfq.dualPathTitle')}
          </h2>
          <p className="text-white/60 text-center mb-16 max-w-2xl mx-auto text-base md:text-lg">
            {t('rfq.dualPathSubtitle')}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Path 1: Browse Inventory */}
            <Link
              to="/products"
              className="card-dark p-8 group hover:border-[#00D4FF]/30 transition-all"
              data-testid="path-browse-inventory"
            >
              <div className="w-14 h-14 flex items-center justify-center bg-[#00D4FF]/10 text-[#00D4FF] mb-6 group-hover:bg-[#00D4FF] group-hover:text-black transition-all">
                <Search className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-bold mb-3">{t('rfq.browsePathTitle')}</h3>
              <p className="text-white/60 text-sm mb-6 leading-relaxed">{t('rfq.browsePathDesc')}</p>
              <span className="text-[#00D4FF] font-semibold text-sm flex items-center gap-2 group-hover:gap-3 transition-all">
                {t('rfq.browsePathCta')} <ArrowRight className="w-4 h-4" />
              </span>
            </Link>

            {/* Path 2: Custom Sourcing */}
            <Link
              to="/contact"
              className="card-dark p-8 group hover:border-[#00D4FF]/30 transition-all"
              data-testid="path-custom-sourcing"
            >
              <div className="w-14 h-14 flex items-center justify-center bg-[#00D4FF]/10 text-[#00D4FF] mb-6 group-hover:bg-[#00D4FF] group-hover:text-black transition-all">
                <Crosshair className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-bold mb-3">{t('rfq.sourcePathTitle')}</h3>
              <p className="text-white/60 text-sm mb-6 leading-relaxed">{t('rfq.sourcePathDesc')}</p>
              <span className="text-[#00D4FF] font-semibold text-sm flex items-center gap-2 group-hover:gap-3 transition-all">
                {t('rfq.sourcePathCta')} <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          </div>
          <p className="text-white/40 text-sm text-center mt-8">{t('rfq.moqNote')}</p>
        </div>
      </section>

      {/* 2. Statistics — Updated labels */}
      <section className="py-12 bg-[#0A0A0A]" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap justify-center items-center gap-12">
            <div className="text-center">
              <div className="text-3xl font-bold gradient-text">20+</div>
              <div className="text-white/50 text-sm">{t('home.yearsExperience')}</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold gradient-text">50,000+</div>
              <div className="text-white/50 text-sm">{t('home.unitsSold')}</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold gradient-text">15+</div>
              <div className="text-white/50 text-sm">{t('home.countries')}</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold gradient-text">100%</div>
              <div className="text-white/50 text-sm">{t('home.qualityTested')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            {t('features.title')}
          </h2>
          <p className="text-white/60 text-center mb-16 max-w-2xl mx-auto">
            {t('features.subtitle')}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, idx) => (
              <div
                key={idx}
                className="card-dark p-8 group hover:neon-glow transition-all duration-300"
                data-testid={`feature-card-${idx}`}
              >
                <div className="w-12 h-12 flex items-center justify-center bg-[#00D4FF]/10 text-[#00D4FF] mb-6 group-hover:bg-[#00D4FF] group-hover:text-black transition-all">
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-semibold mb-3">{feature.title}</h3>
                <p className="text-white/60 text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-24 bg-[#0A0A0A]" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            {t('home.productCategories')}
          </h2>
          <p className="text-white/60 text-center mb-16 max-w-2xl mx-auto">
            {t('home.browseInventory')}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {CATEGORIES.map((cat) => (
              <Link
                key={cat.name}
                to={`/products?category=${cat.name}`}
                className="card-dark p-6 text-center group hover:border-[#00D4FF]/30 transition-all"
                data-testid={`category-${cat.name.toLowerCase()}`}
              >
                <div className="text-2xl font-bold gradient-text mb-2">{cat.name}</div>
                <div className="text-white/80 text-sm mb-1">{cat.desc[language] || cat.desc.en}</div>
                <div className="text-white/40 text-xs font-mono">{cat.count}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Recent Inventory Examples — NEW */}
      <section className="py-24" data-testid="recent-inventory-section">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16">
            {t('home.recentInventoryTitle')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {INVENTORY_EXAMPLES.map((item, idx) => (
              <div key={idx} className="card-dark p-6 flex items-center gap-4" data-testid={`inventory-example-${idx}`}>
                <Monitor className="w-8 h-8 text-[#00D4FF] flex-shrink-0" />
                <div>
                  <div className="font-semibold text-white">
                    {item.brand} {item.model}
                  </div>
                  <div className="text-white/50 text-sm">
                    {item.specs} — Grade {item.grade}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link
              to="/products"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="browse-full-inventory-btn"
            >
              {t('home.browseFullInventory')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Brands */}
      <section className="py-24 bg-[#0A0A0A]" data-testid="brands-section">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16">
            {t('home.trustedBrands')}
          </h2>
          <div className="flex flex-wrap justify-center items-center gap-16">
            {BRANDS.map((brand) => (
              <div
                key={brand.name}
                className="text-4xl font-bold text-white/30 hover:text-white/60 transition-colors cursor-default"
                data-testid={`brand-${brand.name.toLowerCase()}`}
              >
                {brand.name}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Work With Luxor Tek */}
      <section className="py-24" data-testid="why-work-section">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            {t('home.whyWorkTitle')}
          </h2>
          <p className="text-white/60 text-center mb-16 max-w-2xl mx-auto text-base md:text-lg">
            {t('home.whyWorkSubtitle')}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Network, title: t('home.whyRefurbisherNetwork'), desc: t('home.whyRefurbisherNetworkDesc') },
              { icon: MapPin, title: t('home.whyMiamiHub'), desc: t('home.whyMiamiHubDesc') },
              { icon: Package, title: t('home.whyInventory'), desc: t('home.whyInventoryDesc') },
              { icon: DollarSign, title: t('home.whyPricing'), desc: t('home.whyPricingDesc') },
            ].map((item, idx) => (
              <div key={idx} className="card-dark p-8 group hover:neon-glow transition-all duration-300" data-testid={`why-card-${idx}`}>
                <div className="w-12 h-12 flex items-center justify-center bg-[#00D4FF]/10 text-[#00D4FF] mb-6 group-hover:bg-[#00D4FF] group-hover:text-black transition-all">
                  <item.icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-semibold mb-3">{item.title}</h3>
                <p className="text-white/60 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5+6. Wholesale Distribution Only — NEW (combines Who We Work With) */}
      <section className="py-24 bg-[#0A0A0A]" data-testid="wholesale-section">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {t('home.wholesaleTitle')}
          </h2>
          <p className="text-white/60 text-lg mb-8">
            {t('home.wholesaleText')}
          </p>
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {[
              t('home.wholesaleDistributors'),
              t('home.wholesaleWholesalers'),
              t('home.wholesaleResellers'),
              t('home.wholesaleIntegrators'),
              t('home.wholesaleCorporate'),
            ].map((label, idx) => (
              <span
                key={idx}
                className="px-4 py-2 border border-white/10 text-white/70 text-sm"
                data-testid={`wholesale-tag-${idx}`}
              >
                {label}
              </span>
            ))}
          </div>
          <p className="text-white/40 text-sm">
            {t('home.wholesaleMinOrder')}
          </p>
        </div>
      </section>

      {/* How Our Process Works */}
      <section className="py-24" data-testid="process-section">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            {t('home.howWorksTitle')}
          </h2>
          <p className="text-white/60 text-center mb-16 max-w-2xl mx-auto text-base md:text-lg">
            {t('home.howWorksSubtitle')}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              t('home.howStep1'), t('home.howStep2'), t('home.howStep3'),
              t('home.howStep4'), t('home.howStep5'), t('home.howStep6'),
            ].map((step, idx) => (
              <div key={idx} className="flex items-start gap-4 p-6 card-dark" data-testid={`process-step-${idx}`}>
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-[#00D4FF] text-black font-bold text-sm">
                  {idx + 1}
                </div>
                <p className="text-white/80 text-sm leading-relaxed pt-2">{step}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. Typical Order Timeline — NEW */}
      <section className="py-24 bg-[#0A0A0A]" data-testid="order-timeline-section">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16">
            {t('home.orderTimelineTitle')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: t('home.timelineStep1'), time: t('home.timelineStep1Time') },
              { label: t('home.timelineStep2'), time: t('home.timelineStep2Time') },
              { label: t('home.timelineStep3'), time: t('home.timelineStep3Time') },
              { label: t('home.timelineStep4'), time: t('home.timelineStep4Time') },
            ].map((step, idx) => (
              <div key={idx} className="relative" data-testid={`timeline-step-${idx}`}>
                <div className="card-dark p-6 text-center h-full">
                  <div className="w-10 h-10 mx-auto mb-4 flex items-center justify-center bg-[#00D4FF] text-black font-bold text-sm">
                    {idx + 1}
                  </div>
                  <div className="font-semibold text-white mb-2">{step.label}</div>
                  <div className="text-[#00D4FF] text-sm font-mono">{step.time}</div>
                </div>
                {idx < 3 && (
                  <div className="hidden lg:block absolute top-1/2 -right-2 transform -translate-y-1/2 z-10">
                    <ArrowRight className="w-4 h-4 text-white/20" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Elements */}
      <section className="py-16 border-y border-white/5" data-testid="trust-section">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">
            {t('home.trustTitle')}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: Shield, label: t('home.trustCertified') },
              { icon: Users, label: t('home.trustNetwork') },
              { icon: Truck, label: t('home.trustMiami') },
              { icon: CheckCircle, label: t('home.trustSupport') },
            ].map((item, idx) => (
              <div key={idx} className="flex flex-col items-center text-center gap-3" data-testid={`trust-item-${idx}`}>
                <div className="w-12 h-12 flex items-center justify-center bg-[#00D4FF]/10 text-[#00D4FF]">
                  <item.icon className="w-6 h-6" />
                </div>
                <span className="text-white/70 text-sm">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. Export Documentation Included — NEW */}
      <section className="py-24 bg-[#0A0A0A]" data-testid="export-docs-section">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center justify-center gap-3 mb-4">
            <FileText className="w-6 h-6 text-[#00D4FF]" />
            <h2 className="text-3xl md:text-4xl font-bold">{t('home.exportDocsTitle')}</h2>
          </div>
          <p className="text-white/60 text-center mb-10 text-lg">{t('home.exportDocsSubtitle')}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              t('home.exportDoc1'), t('home.exportDoc2'), t('home.exportDoc3'),
              t('home.exportDoc4'), t('home.exportDoc5'), t('home.exportDoc6'),
            ].map((doc, idx) => (
              <div key={idx} className="flex items-center gap-3 p-4 card-dark" data-testid={`export-doc-${idx}`}>
                <CheckCircle className="w-5 h-5 text-[#00D4FF] flex-shrink-0" />
                <span className="text-white/80 text-sm">{doc}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* U.S.-Based Supply Network */}
      <section className="py-24" data-testid="supply-network-section">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <MapPin className="w-6 h-6 text-[#00D4FF]" />
            <h2 className="text-3xl md:text-4xl font-bold">{t('home.supplyNetworkTitle')}</h2>
          </div>
          <p className="text-white/60 text-lg leading-relaxed max-w-3xl mx-auto">
            {t('home.supplyNetworkBody')}
          </p>
        </div>
      </section>

      {/* 9. CTA Section — Updated */}
      <section
        className="py-32 relative"
        style={{
          backgroundImage: `linear-gradient(to right, rgba(5,5,5,0.95), rgba(5,5,5,0.8)), url(https://images.unsplash.com/photo-1672552226650-796f40198c47?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjB3YXJlaG91c2UlMjBzaGVsdmVzJTIwZGFya3xlbnwwfHx8fDE3NzI0NzY4NTJ8MA&ixlib=rb-4.1.0&q=85)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-12" data-testid="cta-title">
            {t('home.ctaTitle')}
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/products" className="btn-primary flex items-center justify-center gap-2" data-testid="cta-browse-btn">
              {t('home.browseProducts')}
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" className="btn-secondary flex items-center justify-center gap-2" data-testid="cta-sourcing-btn">
              <MessageSquare className="w-4 h-4" />
              {t('rfq.customSourcing')}
            </Link>
            <Link to="/quote-list" className="btn-secondary flex items-center justify-center gap-2" data-testid="cta-quote-btn">
              <FileText className="w-4 h-4" />
              {t('home.requestQuote')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
